import { Icon } from '@rsuite/icons';
import React, { useEffect, useRef, useState } from 'react';
import { MdClear, MdDownload, MdMusicNote, MdUpload } from 'react-icons/md';
import { InputGroup, Loader, Tooltip, Whisper } from 'rsuite';
import { ObjectStorageFileCategory, ObjectStorageFileType } from '@/models/storage.model';
import { uploadToStorage } from '@/services/storage.service';
import { AudioPlayer } from 'react-audio-play';
import { AxiosError } from 'axios';
import { BaseErrorModel } from '@/models/core.model';
import { toastErrorMessage } from '@/utils/toasterMessage';


// Define the prop types for AudioUploader
interface AudioUploaderProps {
  objectStorageCategory: ObjectStorageFileCategory; // Pass the category for the file
  onChange?: (url: string | undefined) => void; // Callback to pass the URL or undefined
  url?: string;
  caption?: string;
  downloadable?: boolean
  uploadable?: boolean;
  clearable?: boolean;
  isCompressed?: boolean
}

const AudioUploader: React.FC<AudioUploaderProps> = ({
  objectStorageCategory,
  onChange,
  url,
  caption = 'Upload Audio ...',
  downloadable = false,
  uploadable = true,
  clearable = true,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | undefined>(''); // Store the uploaded audio URL

  useEffect(()=> {
    setAudioUrl(url);
  },[url]);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUploading(true);
      const file = e.target.files[0];
      const upload = uploadToStorage;
      upload({file: file, category: objectStorageCategory, type: ObjectStorageFileType.Sound})          
        .then(data => {
          console.log('Uploaded URL:', data);
          setAudioUrl(data); // Update internal state
          onChange && onChange(data); // Notify the parent of the uploaded URL
        })
        .catch(err => {
          const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
          toastErrorMessage(`Error in uploading audio: ${errorModel?.errorMessage}`);
          fileInputRef.current!.value = '';
        })
        .finally(() => {
          setUploading(false);
        });
    } else {
      setAudioUrl(undefined); // Clear the audio if no file is selected
      onChange && onChange(undefined); // Notify parent of clearing
    }
  };
  
  const triggerFileInput = () => {
    if (uploadable && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const clearAudio = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.stopPropagation();
    setAudioUrl(undefined);
    onChange && onChange(undefined); // Notify parent that the audio is cleared
  };

  const downloadAudio = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>, url: string) => {
  // const downloadAudio = () => {
    e.stopPropagation();
    // if(!audioUrl) return;

    fetch(url)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.blob();
    })
    .then((blob) => {
      // Extract the filename from the URL path
      const urlPath = new URL(url).pathname; // Extracts only the path part of the URL
      const filename = urlPath.substring(urlPath.lastIndexOf('/') + 1) || 'downloaded-file';

      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename; // Use the extracted filename
      document.body.appendChild(link);

      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch((error) => {
      console.error('Error fetching the file:', error);
    });
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        accept=".mp3,.wav,.m4a,.flac,.wma" 
      />
      {!audioUrl && <InputGroup>
        <InputGroup.Addon>
          <Icon as={MdMusicNote}/>
        </InputGroup.Addon>      
        <div style={{ flex: 1, padding: '8px' }}>
          {uploading ? <Loader center content="Uploading..." /> : caption}
        </div>
        <Whisper placement="top" trigger="hover" speaker={<Tooltip>Upload Audio</Tooltip>}>
          <InputGroup.Button onClick={triggerFileInput}>
            <Icon as={MdUpload}/>
          </InputGroup.Button>
        </Whisper>
      </InputGroup>}

      {audioUrl && <InputGroup >
        {downloadable && <Whisper placement="top" trigger="hover" speaker={<Tooltip>Download Audio</Tooltip>}>
          <InputGroup.Button onClick={e => downloadAudio(e, audioUrl)}>
            <Icon as={MdDownload}/>
          </InputGroup.Button>
        </Whisper>
        }
        <div style={{ flex: 1, minHeight:36}}>
          <AudioPlayer width='100%' preload='none' src={audioUrl} style={{
            transform: 'scale(0.8)',
            height: '100%',
            boxShadow:'none',
          }}/>
        </div>
        {clearable && <Whisper placement="top" trigger="hover" speaker={<Tooltip>Clear Audio</Tooltip>}>
          <InputGroup.Button onClick={clearAudio}>
            <Icon as={MdClear}/>
          </InputGroup.Button>
        </Whisper>
        }
      </InputGroup>}
    </>
  );
};

export default AudioUploader;